import { useMemo, useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsList, TabsTrigger } from "../../@/components/ui/tabs";
import { CourseCard } from "../components/courseCards";
import { NewCourseDialog } from "../forms/addCourse";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@clerk/clerk-react";
import { EmptyState } from "../components/emptyState";
import { BookOpenIcon } from "lucide-react";
import { Spinner } from "../components/spinner";
import { useSelector } from "react-redux";
import { apiRequest } from "@/lib/apiRequest";
import { queryKeys } from "@/lib/queryKeys";

function normalizeCoursesPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.courses)) return payload.courses;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export default function ManageCourses() {
  const [tab, setTab] = useState("my");
  const queryClient = useQueryClient();
  const { getToken, isLoaded } = useAuth();
  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const isManager = roles.includes("Manager");

  const safeReadAdminMode = () => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem("zindua_admin_mode") === "on";
    } catch {
      return false;
    }
  };

  const [adminModeOn, setAdminModeOn] = useState(() => (isAdmin ? safeReadAdminMode() : false));
  useEffect(() => {
    if (!isAdmin) {
      setAdminModeOn(false);
      return;
    }
    setAdminModeOn(safeReadAdminMode());
  }, [isAdmin]);

  useEffect(() => {
    const handle = () => {
      if (!isAdmin) return;
      setAdminModeOn(safeReadAdminMode());
    };
    window.addEventListener("zindua-mode-changed", handle);
    return () => window.removeEventListener("zindua-mode-changed", handle);
  }, [isAdmin]);

  const availableTabs = useMemo(() => {
    // Instructors + Managers: never show All Courses.
    // Admins: Admin Mode OFF => My + Managed. Admin Mode ON => All only.
    if (isAdmin) return adminModeOn ? ["all"] : ["my", "managed"];
    return ["my", "managed"];
  }, [isAdmin, adminModeOn]);

  const isAuthReady = Boolean(isLoaded);

  // Public-first: show unauthenticated data ASAP, then upgrade when Clerk is ready.
  const allCoursesPublicQuery = useQuery({
    queryKey: queryKeys.courses.allPublic(),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: "/course/",
        auth: "none",
        signal,
      }).then(normalizeCoursesPayload),
  });

  const allCoursesAuthedQuery = useQuery({
    queryKey: queryKeys.courses.allAuthed(),
    enabled: Boolean(isLoaded),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: "/course/",
        auth: "optional",
        signal,
      }).then(normalizeCoursesPayload),
  });

  const myCoursesQuery = useQuery({
    queryKey: queryKeys.courses.my(),
    enabled: Boolean(isLoaded),
    staleTime: 0,
    refetchOnMount: true,
    queryFn: async ({ signal }) => {
      try {
        const payload = await apiRequest(getToken, {
          path: "/course/tm-courses",
          auth: "required",
          signal,
          retryOnceOn401: true,
        });

        if (import.meta?.env?.DEV) {
          // eslint-disable-next-line no-console
          console.log("[ManageCourses] /course/tm-courses payload:", payload);
        }

        return normalizeCoursesPayload(payload);
      } catch (err) {
        if (import.meta?.env?.DEV) {
          const status = err?.response?.status;
          const data = err?.response?.data;
          // eslint-disable-next-line no-console
          console.log("[ManageCourses] /course/tm-courses error:", {
            status,
            data,
            message: err?.message,
          });
        }
        throw err;
      }
    },
  });

  const managedCoursesQuery = useQuery({
    queryKey: queryKeys.courses.managed(),
    enabled: Boolean(isLoaded),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: "/course/managed",
        auth: "required",
        signal,
      }).then(normalizeCoursesPayload),
  });

  const adminCoursesQuery = useQuery({
    queryKey: queryKeys.courses.admin(),
    enabled: Boolean(isLoaded && isAdmin && adminModeOn),
    queryFn: ({ signal }) =>
      apiRequest(getToken, {
        path: "/course/admin",
        auth: "required",
        signal,
      }).then(normalizeCoursesPayload),
  });

  const allCourses =
    (Array.isArray(allCoursesAuthedQuery.data)
      ? allCoursesAuthedQuery.data
      : null) ??
    (Array.isArray(allCoursesPublicQuery.data)
      ? allCoursesPublicQuery.data
      : null) ??
    [];
  const myCourses = normalizeCoursesPayload(myCoursesQuery.data);
  const managedCourses = Array.isArray(managedCoursesQuery.data)
    ? managedCoursesQuery.data
    : [];
  const adminCourses = Array.isArray(adminCoursesQuery.data)
    ? adminCoursesQuery.data
    : [];

  const filteredCourses = useMemo(() => {
    if (tab === "my") return myCourses;
    if (tab === "managed") return managedCourses;
    return isAdmin ? adminCourses : allCourses;
  }, [tab, myCourses, managedCourses, allCourses, adminCourses, isAdmin]);

  // Sync tab state with URL `tab` query param on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tabParam = params.get("tab");
      const candidate = tabParam === "my" || tabParam === "managed" || tabParam === "all" ? tabParam : null;
      const next = candidate && availableTabs.includes(candidate) ? candidate : availableTabs[0] || "my";
      setTab(next);
    } catch (e) {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep active tab valid when role/mode changes (e.g. admin toggles Admin Mode).
  useEffect(() => {
    if (!availableTabs.includes(tab)) {
      setTab(availableTabs[0] || "my");
    }
  }, [availableTabs, tab]);

  // Update `tab` query param when tab changes
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      params.set("tab", tab);
      const newUrl = window.location.pathname + "?" + params.toString();
      window.history.replaceState({}, "", newUrl);
    } catch (e) {
      // ignore
    }
  }, [tab]);

  const isLoadingCurrentTab =
    (tab === "my" &&
      (!isAuthReady ||
        Boolean((myCoursesQuery.isPending ?? myCoursesQuery.isLoading) || myCoursesQuery.isFetching))) ||
    (tab === "managed" &&
      (!isAuthReady ||
        Boolean(
          (managedCoursesQuery.isPending ?? managedCoursesQuery.isLoading) ||
            managedCoursesQuery.isFetching,
        ))) ||
    (tab === "all" &&
      (isAdmin
        ? !isAuthReady ||
          Boolean(
            (adminCoursesQuery.isPending ?? adminCoursesQuery.isLoading) ||
              adminCoursesQuery.isFetching,
          )
        : allCoursesPublicQuery.isPending));

  const isErrorCurrentTab =
    (tab === "my" && myCoursesQuery.isError) ||
    (tab === "managed" && managedCoursesQuery.isError) ||
    (tab === "all" && isAdmin && adminCoursesQuery.isError) ||
    (tab === "all" && !isAdmin && allCoursesPublicQuery.isError);

  if (isLoadingCurrentTab) {
    return (
      <div className="flex flex-col items-center justify-center min-h-75 w-full">
        <EmptyState
          title={
            <div className="flex items-center justify-center w-full h-full">
              <Spinner />
            </div>
          }
          description="Please wait while we load your courses."
        />
      </div>
    );
  }

  if (isErrorCurrentTab) {
    return (
      <div className="flex flex-col items-center justify-center min-h-75 w-full">
        <EmptyState
          title="Couldn’t load courses"
          description="Something went wrong while loading courses. Please reload and try again."
        />
      </div>
    );
  }

  const refetchCourses = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.courses.my() });
    queryClient.invalidateQueries({ queryKey: queryKeys.courses.managed() });
    queryClient.invalidateQueries({ queryKey: queryKeys.courses.allPublic() });
    queryClient.invalidateQueries({ queryKey: queryKeys.courses.allAuthed() });
    if (isAdmin) {
      queryClient.invalidateQueries({ queryKey: queryKeys.courses.admin() });
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen">
      {/* Page Header */}
      <h1>Manage Courses</h1>
      <p className="text-(--neutral-700)">
        Manage your courses effectively, launch new ones and keep an eye on
        what's published.
      </p>

      {/* Tab Navigation - Allows switching between 'my' and 'all' courses */}
      <Tabs value={tab} onValueChange={setTab} className="space-y-4 mt-3">
        <div className="flex flex-col w-full sm:flex-col md:flex-row md:items-center md:justify-between">
          <TabsList className="w-full md:w-auto">
            {availableTabs.map((value) => (
              <TabsTrigger
                key={value}
                value={value}
                className="w-full md:w-auto"
              >
                {value === "my"
                  ? "My Courses"
                  : value === "managed"
                    ? "Managed Courses"
                    : "All Courses"}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="w-full md:w-auto md:mt-0 mt-4">
            <NewCourseDialog
              refetchCourses={refetchCourses}
            />
          </div>
        </div>

        {/* Display Course Cards with Animation */}
        <AnimatePresence mode="wait">
          <motion.div
            key={tab} // Re-animate when tab changes
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
            exit={{ opacity: 0, y: -10, transition: { duration: 0.5 } }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full"
          >
            {/* Conditional Rendering - Show courses or empty state */}
            {filteredCourses.length === 0 ? (
              <div className="col-span-full">
                <EmptyState
                  icon={BookOpenIcon}
                  title={
                    tab === "my"
                      ? "No Courses Yet"
                      : tab === "managed"
                        ? "No Managed Courses"
                        : "No Courses Available"
                  }
                  description={
                    tab === "my"
                      ? "No courses assigned to you yet. Click below to get started by creating a new course."
                      : tab === "managed"
                        ? "You currently have no courses assigned to you as a manager."
                        : "There are no published courses available right now."
                  }
                  // Action Button - Only show "Create Course" button on 'my' tab, null for 'all' tab
                  action={
                    tab === "my" ? (
                      <div className="flex flex-col items-center justify-center w-full h-full">
                        <NewCourseDialog refetchCourses={refetchCourses} />
                      </div>
                    ) : null
                  }
                />
              </div>
            ) : (
              // Map over filtered courses and render CourseCard for each
              filteredCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  tab={tab}
                  showAdminAllActions={tab === "all" && isAdmin && adminModeOn}
                />
              ))
            )}
          </motion.div>
        </AnimatePresence>
      </Tabs>
    </div>
  );
}
